import { Logger, StepEvent } from '@samelogic/steps';
import { StepConfig } from '../../../models';
import { PackageMemoryCache } from '../Repository/PackageMemoryCache';
import { StepContainer } from '../StepContainer';
import { ConfigTreeNode, StepNode } from './models';
import { buildStepConfigTree } from './utils/buildStepConfigTree';
import { mergeStepEventIntoEventRecords } from './utils/mergeStepEventIntoEventRecords';
import { getDependencyConfigKeys } from './utils/getDependencyConfigKeys';
import { getRootConfigKey } from './utils/getRootConfigKey';
import { Context } from '../../Context/Context';
import { EventLogger } from '../../../tracking/EventLogger';

export class StepContainerManager {
  private readonly stepTree: Map<string, ConfigTreeNode>;

  constructor(
    stepConfigs: StepConfig[],
    private readonly packageCache: PackageMemoryCache,
    private readonly logger: Logger,
    private readonly eventLogger: EventLogger
  ) {
    this.stepTree = buildStepConfigTree(stepConfigs);
  }
  public async start(context: Context): Promise<void> {
    const key = getRootConfigKey(this.stepTree);
    await this.createAndRunStepInstance(key, context);
  }

  private async handleEvent(parent: StepNode, stepEvent: StepEvent, context: Context): Promise<void> {
    const events = mergeStepEventIntoEventRecords(context.events, stepEvent);
    const newContext = context.clone(events);

    // Find config tree nodes that should handle the event
    const dependencyKeys = getDependencyConfigKeys(this.stepTree, stepEvent);
    for (const key of dependencyKeys) {
      await this.createAndRunStepInstance(key, newContext, parent);
    }

    await this.eventLogger.logStepEvent(stepEvent, parent.container);
  }

  private async createAndRunStepInstance(
    configTreeKey: string,
    context: Context,
    parent?: StepNode
  ): Promise<void> {
    const configNode = this.stepTree.get(configTreeKey);
    if (!configNode) {
      throw new Error(`No step found for key: ${configTreeKey}`);
    }

    const stepPackage = await this.packageCache.get(configNode.config.uses);
    const container = new StepContainer(context, stepPackage, configNode.config, this.logger);

    const stepNode = {
      parent: parent,
      container: container
    };
    configNode.stepInstances.push(stepNode);

    container.onEventTriggered = async (stepEvent: StepEvent): Promise<void> => {
      // TODO: remove this when steps are passing in step name in the event
      stepEvent.stepName = configNode.config.name;
      await this.handleEvent(stepNode, stepEvent, context);
    };

    await container.run();
  }
}
